/**
 * IELTS Writing, General Training (D-105): Task 1 (a letter, at least 150 words) and Task 2 (an
 * essay, at least 250 words) in 60 minutes. A plain English editor (no French accent keys), a live
 * word counter against the minimum, automatic saving while typing, submit per task.
 */

import { ApiError, academyAttemptsNextCreate, academyAttemptsTextCreate } from "@agencyos/api-client";
import { type IeltsState, type IeltsTask, pick } from "@agencyos/core";
import { Badge, Button } from "@agencyos/ui-web";
import { ArrowUpFromLine, Check, Clock } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { countWords } from "../text";
import { WritingField } from "./ItemInput";

type SaveState = "idle" | "saving" | "saved" | "error";

function TaskBrief({ task }: { task: IeltsTask }) {
  const { t, i18n } = useTranslation();
  const label = pick(task.label, i18n.language);
  return (
    <div className="ac-task__brief">
      <p className="ac-player__eyebrow">
        {label || t("ielts.writing.task", { n: task.number })}
      </p>
      <div className="ac-ielts-meta">
        {task.letterType && <Badge>{t(`ielts.writing.letter.${task.letterType}`, { defaultValue: task.letterType })}</Badge>}
        {task.minWords !== null && <Badge tone="accent">{t("ielts.writing.minWords", { count: task.minWords })}</Badge>}
        {task.suggestedMinutes !== null && (
          <span className="ac-caption">
            <Clock aria-hidden size={13} /> {t("ielts.writing.suggested", { count: task.suggestedMinutes })}
          </span>
        )}
      </div>
      <p className="ac-prompt ac-ielts-prompt" lang="en">
        {task.prompt}
      </p>
      {task.bullets.length > 0 && (
        <ul className="ac-ielts-bullets" lang="en">
          {task.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function IeltsWritingSection({ attemptId, state, onChanged }: { attemptId: string; state: IeltsState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [texts, setTexts] = useState<Record<string, string>>(() => Object.fromEntries(state.tasks.map((task) => [task.id, task.text])));
  const [saves, setSaves] = useState<Record<string, SaveState>>({});
  const [current, setCurrent] = useState(() => Math.max(0, state.tasks.findIndex((task) => task.status !== "submitted" && task.status !== "graded")));
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const timers = useRef(new Map<string, number>());
  const latest = useRef(texts);
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    latest.current = texts;
  }, [texts]);

  const save = useCallback(
    (taskId: string, submit = false): Promise<void> => {
      window.clearTimeout(timers.current.get(taskId));
      timers.current.delete(taskId);
      queue.current = queue.current.then(async () => {
        setSaves((s) => ({ ...s, [taskId]: "saving" }));
        try {
          await academyAttemptsTextCreate(attemptId, { task: taskId, text: latest.current[taskId] ?? "", submit });
          setSaves((s) => ({ ...s, [taskId]: "saved" }));
        } catch (failure) {
          setSaves((s) => ({ ...s, [taskId]: "error" }));
          if (failure instanceof ApiError && failure.status === 400) await onChanged();
        }
      });
      return queue.current;
    },
    [attemptId, onChanged],
  );

  const flushAll = useCallback(async () => {
    await Promise.all([...timers.current.keys()].map((taskId) => save(taskId)));
  }, [save]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushAll();
    };
    document.addEventListener("visibilitychange", onHide);
    const pending = timers.current;
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      // D-171: the section closes at the end of the time: what was just typed is still sent
      [...pending.keys()].forEach((taskId) => void save(taskId));
    };
  }, [flushAll, save]);

  const onText = (taskId: string, text: string) => {
    setTexts((prev) => ({ ...prev, [taskId]: text }));
    latest.current = { ...latest.current, [taskId]: text };
    window.clearTimeout(timers.current.get(taskId));
    timers.current.set(taskId, window.setTimeout(() => void save(taskId), 1500));
  };

  const submitTask = async (taskId: string) => {
    await save(taskId, true);
    await onChanged();
    const next = state.tasks.findIndex((task, i) => i > current && task.status !== "submitted");
    if (next >= 0) setCurrent(next);
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await flushAll();
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  const task = state.tasks[current];
  const locked = task ? task.status !== "" && task.status !== "draft" : true;
  const saveState = task ? (saves[task.id] ?? "idle") : "idle";

  return (
    <section className="ac-writing" aria-label={t("ielts.skill.ee")}>
      <div className="ac-task-tabs" role="tablist" aria-label={t("academy.writing.tasks")}>
        {state.tasks.map((item, index) => {
          const sent = item.status === "submitted" || item.status === "graded";
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`ac-task-tab${sent ? " ac-task-tab--sent" : ""}`}
              aria-selected={index === current}
              onClick={() => setCurrent(index)}
            >
              <span className="ac-task-tab__n ao-num" aria-hidden="true">
                {sent ? <Check size={13} strokeWidth={3} /> : item.number}
              </span>
              <span>{t("ielts.writing.task", { n: item.number })}</span>
              <span className="ac-task-tab__words ao-num" aria-hidden="true">
                {t("academy.writing.wordsPlain", { count: countWords(texts[item.id] ?? "") })}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ac-question__sheet">
        {task ? (
          <div className="ac-task" key={task.id}>
            <TaskBrief task={task} />
            <WritingField
              value={texts[task.id] ?? ""}
              onChange={(text) => onText(task.id, text)}
              minWords={task.minWords}
              disabled={locked}
              rows={14}
              label={t("ielts.writing.answer", { n: task.number })}
            />
            <p className={`ac-save-state ac-save-state--${locked ? "saved" : saveState}`} aria-live="polite">
              <span className="ac-dot" aria-hidden="true" />
              {locked ? t("academy.writing.submitted") : t(`academy.writing.save.${saveState}`)}
            </p>
            {!locked && (
              <div className="ac-question__actions">
                <Button variant="secondary" icon={<ArrowUpFromLine aria-hidden size={18} />} onClick={() => void submitTask(task.id)}>
                  {t("ielts.writing.submitTask", { n: task.number })}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="ac-secondary">{t("academy.writing.noTask")}</p>
        )}
      </div>

      <p className="ac-tip">{t("ielts.writing.tip")}</p>

      <div className="ac-finish">
        {confirmFinish ? (
          <div className="ac-finish__confirm" role="alertdialog" aria-label={t("ielts.writing.finish")}>
            <p>{t("ielts.writing.finishConfirm")}</p>
            <div className="ac-card__actions">
              <Button variant="ghost" onClick={() => setConfirmFinish(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void finish()} disabled={finishing}>
                {finishing ? t("common.loading") : t("ielts.writing.finishYes")}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmFinish(true)}>
            {t("ielts.writing.finish")}
          </Button>
        )}
      </div>
    </section>
  );
}
